<template>
  <section :class="[$options.name, { mobile: isMobile }, { disabled: busy }]">
    <p v-if="loginError" class="tw:mb-2" style="font-size: 0.875rem; color: red">
      {{ loginError }}
    </p>
    <EdroneInput
      id="email-input"
      v-model.trim="v$.userEmail.$model"
      :error-msg="
        v$.userEmail.$dirty && v$.userEmail.$error
          ? translations.inputs.email.errors[v$.userEmail.$errors[0].$message]
          : null
      "
      :invalid="v$.userEmail.$dirty && (v$.userEmail.email.$invalid || v$.userEmail.required.$invalid)"
      :label="translations.inputs.email.label"
      :placeholder="translations.inputs.email.placeholder"
      class="required-inputs tw:mb-4"
      type="email"
      unique-id="b07130e5-2251-4b5c-9cac-4c020163f842"
      @enter="handleNextInput"
    />
    <EdroneInput
      v-model.trim="v$.userPassword.$model"
      :error-msg="
        v$.userPassword.$dirty && v$.userPassword.$error
          ? translations.inputs.password.errors[v$.userPassword.$errors[0].$message]
          : null
      "
      :invalid="v$.userPassword.$dirty && v$.userPassword.required.$invalid"
      :label="translations.inputs.password.label"
      :placeholder="translations.inputs.password.placeholder"
      class="required-inputs tw:mb-2"
      type="password"
      unique-id="d8c3547a-b608-4734-ac0e-a3dc7d16105a"
      @enter="handleNextInput"
    />

    <a
      href="/forgot_password"
      class="tw:text-blue-500 tw:text-sm tw:no-underline tw:float-right tw:mb-4"
    >
      {{ translations.buttons.password }}
    </a>

    <EdroneButton
      :disabled="!userEmail || !userPassword"
      class="required-inputs tw:mb-4"
      style="width: 100%"
      type="solid"
      unique-id="c4291173-4ca9-41dc-bbd3-205a7362420a"
      @click="printResult"
    >
      {{ translations.buttons.login }}
    </EdroneButton>

    <div class="tw:flex tw:items-center tw:gap-3 tw:mb-4">
      <div class="tw:flex-1 tw:h-px tw:bg-gray-300"></div>
      <span class="tw:text-gray-500 tw:text-sm">{{ translations.google?.or || "or" }}</span>
      <div class="tw:flex-1 tw:h-px tw:bg-gray-300"></div>
    </div>

    <GoogleSignInButton
      :translations="translations.google"
      class="tw:mb-4"
      context="login"
      @success="handleGoogleSignIn"
      @error="handleGoogleError"
    />

    <p v-if="!isEnabledWebView" class="tw:text-sm tw:text-center">
      {{ translations.register.text }}
      <a :href="'/register' + query" class="tw:text-blue-500 tw:no-underline" @click="$emit('register')">
        {{ translations.register.action }}
      </a>
    </p>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import axios from "vue-app/utils/axios";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";
import { useCaptcha } from "vue-app/composables/useCaptcha.ts";
import { type GoogleUserData } from "vue-app/composables/useGoogleSignIn/useGoogleSignIn.composable";
import GoogleSignInButton from "vue-app/components/Account/GoogleSignInButton/GoogleSignInButton.vue";

export default defineComponent({
  name: "AccountLoginForm",
  components: {
    EdroneInput,
    EdroneButton,
    GoogleSignInButton,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["register"],
  setup() {
    const query = window.location.search;
    const translations = window._trans._account.login;
    const refRecaptchaDiv = ref(null);

    const userEmail = ref("");
    const userPassword = ref("");
    const loginError = ref("");

    const rules = {
      userEmail: { required, email },
      userPassword: { required },
    };
    const v$ = useVuelidate(rules, { userEmail, userPassword });

    function handleNextInput(event: KeyboardEvent) {
      const inputs = document.querySelectorAll(".required-inputs input, .required-inputs.edrone-button");
      const index = Array.from(inputs).indexOf(event.target as HTMLElement);
      if (index === 0) {
        v$.value.userEmail.$touch();
      }
      if (index === 1) {
        v$.value.userPassword.$touch();
      }
      (inputs[index + 1] as HTMLElement)?.focus();
      if (index === 1 && !!userPassword.value) {
        printResult();
      }
    }

    const busy = ref<boolean>(false);

    function printResult() {
      v$.value.userEmail.$touch();
      v$.value.userPassword.$touch();

      if (!v$.value.userEmail.$error && !v$.value.userPassword.$error) {
        loginUser();
      }
    }

    async function loginUser() {
      try {
        amplitudeSendEvent("login-button-click");
        const { executeRecaptcha } = useCaptcha();
        const token = await executeRecaptcha("LOGIN");
        if (!token) {
          loginError.value = translations.errors.other;
          busy.value = false;
          return;
        }
        busy.value = true;
        loginError.value = "";
        let bodyFormData = new FormData();
        bodyFormData.append("email", userEmail.value);
        bodyFormData.append("password", userPassword.value);
        bodyFormData.append("g-recaptcha-response", token);
        bodyFormData.append("_remember_me", "true");

        const response = await axios({
          method: "post",
          url: "/login",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          amplitudeSendEvent("login-success");
          window.location.href = "/login/trackers";
        } else {
          amplitudeSendEvent("login-error");

          if (response.data.status === "error") {
            if (response.data.message === "Your email address is not assigned to any workspace") {
              loginError.value = translations.errors.unlinked;
            } else {
              loginError.value = translations.errors.invalid;
            }
          } else {
            loginError.value = translations.errors.expired;
          }
          busy.value = false;
        }
      } catch (error) {
        loginError.value = translations.errors.other;
        busy.value = false;
      }
    }

    async function handleGoogleSignIn(userData: GoogleUserData) {
      busy.value = true;
      loginError.value = "";
      amplitudeSendEvent("google-login-button-click");

      try {
        const bodyFormData = new FormData();
        bodyFormData.append("google_token", userData.token);

        const response = await axios({
          method: "post",
          url: "/login/google",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          amplitudeSendEvent("google-login-success");
          window.location.href = "/login/trackers";
        } else {
          amplitudeSendEvent("google-login-error", { error_type: response.data.error_type });
          
          if (response.data.error_type === "email_exists_not_linked") {
            loginError.value = translations.google?.emailExistsNotLinked || 
              "An account with this email exists. Please log in with your password and link your Google account in settings.";
          } else if (response.data.error_type === "account_not_found") {
            loginError.value = translations.google?.accountNotFound || 
              "No account found with this email. Please register first.";
          } else {
            loginError.value = response.data.message || translations.errors.other;
          }
          busy.value = false;
        }
      } catch (error) {
        amplitudeSendEvent("google-login-error", { error_type: "network_error" });
        loginError.value = translations.errors.other;
        busy.value = false;
      }
    }

    function handleGoogleError(errorMessage: string) {
      loginError.value = errorMessage;
      busy.value = false;
    }

    const isEnabledWebView = ReactNativeBridge.checkIsWebViewMode();

    return {
      query,
      printResult,
      refRecaptchaDiv,
      busy,
      translations,
      userEmail,
      userPassword,
      v$,
      handleNextInput,
      loginError,
      isEnabledWebView,
      handleGoogleSignIn,
      handleGoogleError,
    };
  },
});
</script>
